import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, Table, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';

const Marge = ({ currentUser }) => {
  const [bills, setBills] = useState([]);
  const [billsPin, setBillsPin] = useState([]); // Factures de l'application pin
  const [products, setProducts] = useState([]);
  const [productsPin, setProductsPin] = useState([]); // Produits de l'application pin
  const [invoices, setInvoices] = useState([]);
  const [technicalSheets, setTechnicalSheets] = useState([]); // Fiches techniques
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    location: '',
    search: ''
  });

  // 🔍 Fonction pour extraire l'ID de localisation
  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === "string") return loc;
    if (loc.$oid) return loc.$oid;
    if (loc._id) return loc._id.toString();
    return null;
  };

  // 📊 Charger toutes les données nécessaires
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem("token");
      
      const [
        billsRes, 
        productsRes, 
        invoicesRes, 
        locationsRes,
        billsPinRes,
        productsPinRes,
        sheetsRes
      ] = await Promise.all([
        axios.get("/api/BillsValises/getAllBillsV", { 
          headers: { Authorised: token } 
        }),
        axios.get("/api/ProductV/getProductV", { 
          headers: { Authorised: token } 
        }),
        axios.get("/api/InvoicesV/getInvoices?populate=subProductV"),
        axios.get("/api/Locations/getAllLocations"),
        axios.get("/api/Bills/getAllBills", { 
          headers: { Authorised: token } 
        }),
        axios.get("/api/Product/getProduct", { 
          headers: { Authorised: token } 
        }),
        axios.get("/api/TechnicalSheet/all-sheets", { 
          headers: { Authorised: token } 
        })
      ]);

      setBills(billsRes.data || []);
      setProducts(productsRes.data || []);
      setInvoices(invoicesRes.data || []);
      setLocations(locationsRes.data || []);
      setBillsPin(billsPinRes.data || []);
      setProductsPin(productsPinRes.data || []);
      setTechnicalSheets(sheetsRes.data || []);

      console.log("📦 Factures d'achat chargées:", invoicesRes.data?.length);
      console.log("📦 Ventes chargées:", billsRes.data?.length);
      console.log("📦 Ventes Pin chargées:", billsPinRes.data?.length);

    } catch (err) {
      console.error("❌ Erreur chargement données:", err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // 🔥 CORRECTION: Utiliser directement le pricettc depuis les factures d'achat (Application 1)
  const findPurchasePrice = useCallback((productId, subProductId) => {
    // Rechercher dans les factures d'achat (invoices)
    for (const invoice of invoices) {
      if (!invoice.items || !Array.isArray(invoice.items)) continue;

      for (const item of invoice.items) {
        const itemProductId = item.productId?.toString();
        const itemSubProductId = item.subProductId?.toString();
        const itemProductV = item.productV?._id?.toString() || item.productV?.toString();
        const itemSubProductV = item.subProductV?._id?.toString() || item.subProductV?.toString();

        // Correspondance par sous-produit
        if (subProductId) {
          const searchSubProductId = subProductId.toString();
          
          if ((itemSubProductId && itemSubProductId === searchSubProductId) ||
              (itemSubProductV && itemSubProductV === searchSubProductId)) {
            
            let purchasePriceTTC;
            if (item.pricettc && item.pricettc > 0) {
              purchasePriceTTC = item.pricettc;
            } else {
              const purchasePriceHT = item.price || 0;
              const tvaRate = item.tva || 0;
              purchasePriceTTC = purchasePriceHT * (1 + tvaRate / 100);
            }
            return purchasePriceTTC || 0;
          }
        }
        
        // Correspondance par produit
        if (productId) {
          const searchProductId = productId.toString();
          
          if ((itemProductId && itemProductId === searchProductId) ||
              (itemProductV && itemProductV === searchProductId)) {
            
            let purchasePriceTTC;
            if (item.pricettc && item.pricettc > 0) {
              purchasePriceTTC = item.pricettc;
            } else {
              const purchasePriceHT = item.price || 0;
              const tvaRate = item.tva || 0;
              purchasePriceTTC = purchasePriceHT * (1 + tvaRate / 100);
            }
            return purchasePriceTTC || 0;
          }
        }
      }
    }
    return 0;
  }, [invoices]);

  // 🔍 Obtenir les détails d'un produit avec le bon prix d'achat TTC (Application 1)
  const getProductDetailsApp1 = useCallback((item) => {
    // Chercher comme produit principal
    const mainProduct = products.find((p) => p._id === item.id);
    
    if (mainProduct) {
      let purchasePriceTTC;
      
      if (mainProduct.category === "SET" && mainProduct.subProductsV?.length > 0) {
        purchasePriceTTC = mainProduct.subProductsV.reduce((total, subProduct) => {
          const subProductPurchasePrice = findPurchasePrice(mainProduct._id, subProduct._id);
          return total + subProductPurchasePrice;
        }, 0);
      } else {
        purchasePriceTTC = findPurchasePrice(mainProduct._id, null);
      }
      
      return {
        _id: mainProduct._id,
        parent: mainProduct.name,
        name: null,
        sellingPrice: item.price || mainProduct.price,
        purchasePrice: purchasePriceTTC,
        localisation: extractLocId(mainProduct.localisation),
        hasSubProduct: false,
        isSet: mainProduct.category === "SET",
        type: 'achat' // Nouveau: type pour identifier l'application
      };
    }

    // Chercher comme sous-produit
    for (const product of products) {
      const subProduct = product.subProductsV?.find((sp) => sp._id === item.id);
      if (subProduct) {
        const purchasePriceTTC = findPurchasePrice(product._id, subProduct._id);
        return {
          _id: subProduct._id,
          parent: product.name,
          name: subProduct.name,
          sellingPrice: item.price || subProduct.price,
          purchasePrice: purchasePriceTTC,
          localisation: extractLocId(subProduct.localisation),
          hasSubProduct: true,
          isSet: false,
          type: 'achat'
        };
      }
    }

    // Produit inconnu
    return {
      _id: item.id,
      name: item.name || "Produit inconnu",
      parent: null,
      sellingPrice: item.price || 0,
      purchasePrice: 0,
      localisation: null,
      hasSubProduct: false,
      isSet: false,
      type: 'achat'
    };
  }, [products, findPurchasePrice]);

  // ==================== LOGIQUE APPLICATION 2 (PRODUCTION) ====================

  // 🔍 Trouver le nom d'un produit (Application 2)
  const findProductNameByIdApp2 = useCallback((productId) => {
    if (!productId) return "Produit inconnu";

    const productIdStr = extractLocId(productId);

    for (const product of productsPin) {
      if (product.subProducts) {
        const subProduct = product.subProducts.find(sp => 
          extractLocId(sp._id) === productIdStr
        );
        if (subProduct) return subProduct.name;
      }
    }

    const mainProduct = productsPin.find(p => 
      extractLocId(p._id) === productIdStr
    );
    if (mainProduct) return mainProduct.name;

    return "Produit inconnu";
  }, [productsPin]);

  // 🔥 Trouver la localisation d'un produit (Application 2)
  const findProductLocationApp2 = useCallback((productId) => {
    if (!productId) return null;

    const productIdStr = extractLocId(productId);

    // 1. Chercher comme sous-produit
    for (const product of productsPin) {
      if (product.subProducts) {
        const subProduct = product.subProducts.find(sp => 
          extractLocId(sp._id) === productIdStr
        );
        if (subProduct) {
          return extractLocId(subProduct.localisation);
        }
      }
    }

    // 2. Chercher comme produit principal
    const mainProduct = productsPin.find(p => 
      extractLocId(p._id) === productIdStr
    );
    if (mainProduct) {
      return extractLocId(mainProduct.localisation);
    }

    return null;
  }, [productsPin]);

  // 🔥 Trouver le coût de production (Application 2)
  const findProductionCost = useCallback((productId) => {
    if (!productId) return 0;

    const productIdStr = extractLocId(productId);

    const sheet = technicalSheets.find(sheet => 
      extractLocId(sheet.subProduct) === productIdStr ||
      extractLocId(sheet.product) === productIdStr
    );

    if (sheet) {
      return sheet.totalCost || 0;
    }

    // Coût estimé à 70% du prix de vente
    let defaultCost = 0;
    for (const product of productsPin) {
      const subProduct = product.subProducts?.find(sp => 
        extractLocId(sp._id) === productIdStr
      );
      if (subProduct && subProduct.price) {
        defaultCost = subProduct.price * 0.7;
        break;
      }
    }

    return defaultCost;
  }, [technicalSheets, productsPin]);

  // 🔍 Obtenir les détails d'un produit (Application 2)
  const getProductDetailsApp2 = useCallback((item) => {
    const productName = findProductNameByIdApp2(item.id);
    const localisation = findProductLocationApp2(item.id);
    const productionCost = findProductionCost(item.id);

    return {
      _id: item.id,
      parent: productName,
      name: null,
      sellingPrice: item.price || 0,
      purchasePrice: productionCost, // Coût de production
      localisation: localisation,
      hasSubProduct: false,
      isSet: false,
      type: 'production' // Nouveau: type pour identifier l'application
    };
  }, [findProductNameByIdApp2, findProductLocationApp2, findProductionCost]);

  // 📈 Calculer les marges avec les bons prix (COMBINÉ)
  const calculateMargins = useCallback(() => {
    const marginsByLocation = {};
    let totalMargin = 0;
    let totalSales = 0;
    let totalCost = 0;

    // Filtrer les factures par date si spécifiée
    const filteredBillsApp1 = bills.filter(bill => {
      const billDate = new Date(bill.date);
      
      if (filters.startDate && billDate < new Date(filters.startDate)) {
        return false;
      }
      if (filters.endDate && billDate > new Date(filters.endDate)) {
        return false;
      }
      return true;
    });

    const filteredBillsApp2 = billsPin.filter(bill => {
      const billDate = new Date(bill.date);
      
      if (filters.startDate && billDate < new Date(filters.startDate)) {
        return false;
      }
      if (filters.endDate && billDate > new Date(filters.endDate)) {
        return false;
      }
      return true;
    });

    console.log("📊 Factures filtrées App1:", filteredBillsApp1.length);
    console.log("📊 Factures filtrées App2:", filteredBillsApp2.length);

    // Traiter chaque facture de vente (Application 1)
    filteredBillsApp1.forEach(bill => {
      bill.items.forEach(item => {
        const details = getProductDetailsApp1(item);
        const quantity = item.quantity || 1;
        
        // Vérifier le filtre de localisation
        const itemLocId = extractLocId(details.localisation);
        if (filters.location && itemLocId !== filters.location) {
          return;
        }

        // Vérifier le filtre de recherche (nom ou sous-produit)
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const productName = (details.parent || "").toLowerCase();
          const subProductName = (details.name || "").toLowerCase();
          if (!productName.includes(searchLower) && !subProductName.includes(searchLower)) {
            return;
          }
        }

        // Calculs basés sur les prix TTC
        const salesAmount = details.sellingPrice * quantity;
        const costAmount = details.purchasePrice * quantity;
        const margin = salesAmount - costAmount;
        const marginPercentage = salesAmount > 0 ? (margin / salesAmount) * 100 : 0;

        // Regrouper par localisation
        const locationId = itemLocId || 'unknown';
        const locationName = locations.find(loc => extractLocId(loc._id) === locationId)?.name || 'Localisation inconnue';

        if (!marginsByLocation[locationId]) {
          marginsByLocation[locationId] = {
            name: locationName,
            sales: 0,
            cost: 0,
            margin: 0,
            percentage: 0,
            items: []
          };
        }

        marginsByLocation[locationId].sales += salesAmount;
        marginsByLocation[locationId].cost += costAmount;
        marginsByLocation[locationId].margin += margin;
        
        // Ajouter l'item détaillé
        marginsByLocation[locationId].items.push({
          date: bill.date,
          product: details.parent || details.name,
          subProduct: details.name,
          quantity,
          sellingPrice: details.sellingPrice,
          purchasePrice: details.purchasePrice,
          salesAmount,
          costAmount,
          margin,
          marginPercentage,
          type: details.type,
          tableNumber: bill.tableNumber || 'N/A'
        });

        // Totaux généraux
        totalSales += salesAmount;
        totalCost += costAmount;
        totalMargin += margin;
      });
    });

    // Traiter chaque facture de vente (Application 2)
    filteredBillsApp2.forEach(bill => {
      bill.items.forEach(item => {
        const details = getProductDetailsApp2(item);
        const quantity = item.quantity || 1;
        
        // Vérifier le filtre de localisation
        const itemLocId = extractLocId(details.localisation);
        if (filters.location && itemLocId !== filters.location) {
          return;
        }

        // Vérifier le filtre de recherche (nom ou sous-produit)
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const productName = (details.parent || "").toLowerCase();
          const subProductName = (details.name || "").toLowerCase();
          if (!productName.includes(searchLower) && !subProductName.includes(searchLower)) {
            return;
          }
        }

        // Calculs basés sur les coûts de production
        const salesAmount = details.sellingPrice * quantity;
        const costAmount = details.purchasePrice * quantity;
        const margin = salesAmount - costAmount;
        const marginPercentage = salesAmount > 0 ? (margin / salesAmount) * 100 : 0;

        // Regrouper par localisation
        const locationId = itemLocId || 'unknown';
        const locationName = locations.find(loc => extractLocId(loc._id) === locationId)?.name || 'Localisation inconnue';

        if (!marginsByLocation[locationId]) {
          marginsByLocation[locationId] = {
            name: locationName,
            sales: 0,
            cost: 0,
            margin: 0,
            percentage: 0,
            items: []
          };
        }

        marginsByLocation[locationId].sales += salesAmount;
        marginsByLocation[locationId].cost += costAmount;
        marginsByLocation[locationId].margin += margin;
        
        // Ajouter l'item détaillé
        marginsByLocation[locationId].items.push({
          date: bill.date,
          product: details.parent || details.name,
          subProduct: details.name,
          quantity,
          sellingPrice: details.sellingPrice,
          purchasePrice: details.purchasePrice,
          salesAmount,
          costAmount,
          margin,
          marginPercentage,
          type: details.type,
          tableNumber: bill.tableNumber || 'N/A'
        });

        // Totaux généraux
        totalSales += salesAmount;
        totalCost += costAmount;
        totalMargin += margin;
      });
    });

    // Calculer les pourcentages par localisation
    Object.keys(marginsByLocation).forEach(locId => {
      const locData = marginsByLocation[locId];
      locData.percentage = locData.sales > 0 ? (locData.margin / locData.sales) * 100 : 0;
    });

    console.log("📈 Résultats marge combinée:", {
      totalSales,
      totalCost,
      totalMargin,
      locations: Object.keys(marginsByLocation).length
    });

    return {
      byLocation: marginsByLocation,
      totals: {
        sales: totalSales,
        cost: totalCost,
        margin: totalMargin,
        percentage: totalSales > 0 ? (totalMargin / totalSales) * 100 : 0
      }
    };
  }, [
    bills, 
    billsPin, 
    filters, 
    getProductDetailsApp1, 
    getProductDetailsApp2, 
    locations
  ]);

  const marginsData = calculateMargins();

  // 📊 Statistiques résumées
  const getStatsSummary = () => {
    const locationsCount = Object.keys(marginsData.byLocation).length;
    const totalItems = Object.values(marginsData.byLocation).reduce(
      (sum, loc) => sum + loc.items.length, 0
    );

    // Calculer les totaux par type
    const totalsByType = { achat: 0, production: 0 };
    Object.values(marginsData.byLocation).forEach(loc => {
      loc.items.forEach(item => {
        if (item.type === 'achat') {
          totalsByType.achat++;
        } else if (item.type === 'production') {
          totalsByType.production++;
        }
      });
    });

    return {
      locationsCount,
      totalItems,
      totalsByType,
      ...marginsData.totals
    };
  };

  const stats = getStatsSummary();

  if (loading) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
        <p className="mt-2">Calcul des marges en cours...</p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4">📈 État des Marges (Combiné)</h2>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Filtres */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Filtres</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Date de début</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Date de fin</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Localisation</Form.Label>
                <Form.Select
                  value={filters.location}
                  onChange={(e) => setFilters({...filters, location: e.target.value})}
                >
                  <option value="">Toutes les localisations</option>
                  {locations.map(loc => (
                    <option key={loc._id} value={extractLocId(loc._id)}>
                      {loc.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Filtrer par Produit / Sous-produit</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Rechercher..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Résumé général */}
      <Card className="mb-4 bg-light">
        <Card.Body>
          <Row>
            <Col md={3} className="text-center">
              <h6>Chiffre d'Affaires</h6>
              <h4 className="text-primary">{stats.sales.toFixed(2)} DT</h4>
              <small className="text-muted">
                📦 Achat: {stats.totalsByType.achat} ventes
              </small>
              <br />
              <small className="text-muted">
                🏭 Production: {stats.totalsByType.production} ventes
              </small>
            </Col>
            <Col md={3} className="text-center">
              <h6>Coût Total</h6>
              <h4 className="text-danger">{stats.cost.toFixed(2)} DT</h4>
            </Col>
            <Col md={3} className="text-center">
              <h6>Marge Totale</h6>
              <h4 className={stats.margin >= 0 ? "text-success" : "text-danger"}>
                {stats.margin.toFixed(2)} DT
              </h4>
            </Col>
            <Col md={3} className="text-center">
              <h6>Pourcentage Marge</h6>
              <h4 className={stats.percentage >= 0 ? "text-success" : "text-danger"}>
                {stats.percentage.toFixed(1)} %
              </h4>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col className="text-center">
              <small className="text-muted">
                {stats.locationsCount} localisation(s) • {stats.totalItems} ventes totales
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Détails par localisation (GARDER L'AFFICHAGE D'ORIGINE) */}
      {Object.entries(marginsData.byLocation).map(([locId, data]) => (
        <Card key={locId} className="mb-4">
          <Card.Header className="bg-secondary text-white">
            <h5 className="mb-0">
              📍 {data.name}
              <span className="float-end">
                Marge: {data.margin.toFixed(2)} DT ({data.percentage.toFixed(1)}%)
              </span>
            </h5>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Table</th>
                  <th>Type</th>
                  <th>Produit</th>
                  <th>Sous-produit</th>
                  <th>Quantité</th>
                  <th>Prix Vente</th>
                  <th>Coût</th>
                  <th>CA</th>
                  <th>Coût Total</th>
                  <th>Marge</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item, index) => (
                  <tr key={index}>
                    <td>{new Date(item.date).toLocaleDateString('fr-FR')}</td>
                    <td>{item.tableNumber}</td>
                    <td>
                      <span className={`badge ${item.type === 'achat' ? 'bg-info' : 'bg-warning'}`}>
                        {item.type === 'achat' ? '📦 Achat' : '🏭 Production'}
                      </span>
                    </td>
                    <td>{item.product}</td>
                    <td>{item.subProduct || '-'}</td>
                    <td>{item.quantity}</td>
                    <td>{item.sellingPrice.toFixed(2)} DT</td>
                    <td>{item.purchasePrice.toFixed(2)} DT</td>
                    <td>{item.salesAmount.toFixed(2)} DT</td>
                    <td>{item.costAmount.toFixed(2)} DT</td>
                    <td className={item.margin >= 0 ? "text-success" : "text-danger"}>
                      {item.margin.toFixed(2)} DT
                    </td>
                    <td className={item.marginPercentage >= 0 ? "text-success" : "text-danger"}>
                      {item.marginPercentage.toFixed(1)}%
                    </td>
                  </tr>
                ))}
                {/* Total de la localisation */}
                <tr className="table-info fw-bold">
                  <td colSpan="8">Total {data.name}</td>
                  <td>{data.sales.toFixed(2)} DT</td>
                  <td>{data.cost.toFixed(2)} DT</td>
                  <td className={data.margin >= 0 ? "text-success" : "text-danger"}>
                    {data.margin.toFixed(2)} DT
                  </td>
                  <td className={data.percentage >= 0 ? "text-success" : "text-danger"}>
                    {data.percentage.toFixed(1)}%
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ))}

      {Object.keys(marginsData.byLocation).length === 0 && !loading && (
        <Alert variant="info" className="text-center">
          Aucune donnée de marge trouvée pour les critères sélectionnés.
        </Alert>
      )}
    </div>
  );
};

export default Marge;